import { Box, Flex, Text, Stack } from '@chakra-ui/react';
import { FastForward, Pause, Play, Rewind } from '@phosphor-icons/react';
import { memo, useCallback } from 'react';

interface VideoControlsProps {
  playing: boolean;
  onPlayPause: () => void;
  onSeek: (seconds: number) => void;
  played: number;
  loaded: number;
  duration: number;
  smoothProgress: number;
}

const VideoControls = memo(
  ({
    playing,
    onPlayPause,
    onSeek,
    played,
    duration,
    loaded,
    smoothProgress
  }: VideoControlsProps) => {
    // Format time in MM:SS format
    const formatTime = useCallback((seconds: number) => {
      if (!seconds || isNaN(seconds) || !isFinite(seconds)) {
        return '00:00';
      }
      const mins = Math.floor(seconds / 60);
      const secs = Math.floor(seconds % 60);
      return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }, []);

    // Handle progress bar click
    const handleProgressClick = (e: React.MouseEvent<HTMLDivElement>) => {
      if (!loaded || loaded <= 0) return;

      const progressBar = e.currentTarget;
      const rect = progressBar.getBoundingClientRect();
      const clickPosition = e.clientX - rect.left;
      const progressBarWidth = rect.width;
      const percentage = Math.max(0, Math.min(1, clickPosition / progressBarWidth));
      if (percentage > loaded / duration) return;

      // Calculate the exact time to seek to based on the click position
      const seekTime = percentage * duration;
      onSeek(seekTime);
    };

    return (
      <Stack gap="12px">
        <Box
          w="100%"
          h="8px"
          bg="neutrals.200"
          borderRadius="100px"
          position="relative"
          onClick={handleProgressClick}
        >
          <Box
            position="absolute"
            left="0"
            top="0"
            h="100%"
            bg="neutrals.300"
            borderRadius="100px"
            cursor={duration > 0 ? 'pointer' : 'default'}
            width={`${duration > 0 ? Math.min((loaded / duration) * 100, 100) : 0}%`}
            transition={playing ? 'none' : 'width 0.1s ease-in-out'}
          >
            <Box
              position="absolute"
              left="0"
              top="0"
              h="100%"
              bg="primary.700"
              borderRadius="100px"
              width={`${
                duration > 0 && loaded > 0
                  ? Math.min((smoothProgress / Math.min(loaded / duration, 1)) * 100, 100)
                  : 0
              }%`}
              transition={playing ? 'none' : 'width 0.1s ease-in-out'}
            />
          </Box>
        </Box>

        <Flex justify="space-between" align="center">
          <Text fontSize="12px" color="neutrals.500">
            {duration > 0 ? formatTime(played) : '00:00'}
          </Text>

          <Text fontSize="12px" color="neutrals.500">
            {formatTime(duration)}
          </Text>
        </Flex>
        <Flex align="center" gap="16px" alignSelf="center">
          <Box as="button" onClick={() => onSeek(played - 10)} _hover={{ cursor: 'pointer' }}>
            <Rewind size={24} color="#242424" weight="fill" />
          </Box>

          <Box as="button" onClick={onPlayPause} _hover={{ cursor: 'pointer' }}>
            <Text fontSize="16px">
              {playing ? (
                <Pause size={24} color="#242424" weight="fill" />
              ) : (
                <Play size={24} color="#242424" weight="fill" />
              )}
            </Text>
          </Box>

          <Box as="button" onClick={() => onSeek(played + 10)} _hover={{ cursor: 'pointer' }}>
            <FastForward size={24} color="#242424" weight="fill" />
          </Box>
        </Flex>
      </Stack>
    );
  }
);

VideoControls.displayName = 'VideoControls';

export default VideoControls;
